import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { GlobalSearch } from "@/components/GlobalSearch";
import {
  LayoutDashboard,
  FileText,
  Wrench,
  ReceiptText,
  Banknote,
  Users,
  LogOut,
  Package,
  Settings,
  Truck,
  Scale,
} from "lucide-react";

const nav = [
  { to: "/", label: "Panel", icon: LayoutDashboard, end: true, id: "dashboard", module: "dashboard" },
  { to: "/cotizaciones", label: "Cotizaciones", icon: FileText, id: "quotes", module: "cotizaciones" },
  { to: "/remisiones", label: "Remisiones", icon: Truck, id: "remisiones", module: "remisiones" },
  { to: "/ordenes", label: "Órdenes de Trabajo", icon: Wrench, id: "orders", module: "ordenes" },
  { to: "/facturas", label: "Facturación", icon: ReceiptText, id: "invoices", module: "facturas" },
  { to: "/cobranza", label: "Cobranza", icon: Banknote, id: "collections", module: "cobranza" },
  { to: "/conciliar", label: "Conciliar", icon: Scale, id: "reconciliation", module: "conciliar" },
  { to: "/productos", label: "Productos", icon: Package, id: "products", module: "productos" },
  { to: "/clientes", label: "Clientes", icon: Users, id: "clients", module: "clientes" },
  { to: "/configuracion", label: "Configuración", icon: Settings, id: "settings", module: "configuracion" },
];

const ROLE_LABELS = { admin: "Administrador", ventas: "Ventas", produccion: "Producción", cobranza: "Cobranza", custom: "Personalizado" };

export function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleNav = nav.filter((n) => can(n.module));

  return (
    <div className="flex min-h-screen bg-[#EEF2F6] dark:bg-[#0A1626]">
      <aside
        className="group no-print shrink-0 border-r border-[#374A66] bg-[#465B7F] flex flex-col fixed h-screen z-40 overflow-hidden w-20 hover:w-64 transition-all duration-200"
        data-testid="sidebar"
      >
        <div className="border-b border-white/20 bg-[#465B7F] flex items-center justify-center px-2 py-4 group-hover:px-5 group-hover:py-5 transition-all duration-200">
          <img
            src="/tas-logo-dark.webp"
            alt="TAS Publicidad 360"
            data-testid="sidebar-logo"
            className="w-12 h-12 object-contain group-hover:w-full group-hover:h-auto group-hover:max-h-24 transition-all duration-200"
          />
        </div>
        <div className="h-1 brand-gradient" />

        <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
          <GlobalSearch />
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              title={n.label}
              className={({ isActive }) =>
                `flex items-center gap-3 py-3 text-base font-bold border-l-2 transition-colors duration-150 justify-center group-hover:justify-start px-0 group-hover:px-6 ${
                  isActive
                    ? "border-white bg-white/15 text-white"
                    : "border-transparent text-white/80 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon size={20} strokeWidth={1.75} className="shrink-0" />
              <span className="hidden group-hover:inline truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/20 p-4">
          <div className="hidden group-hover:block">
            <div className="font-mono-data text-base text-white truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm text-white/70 truncate" data-testid="current-user-email">{user?.email}</span>
              <span className="text-xs uppercase tracking-wider bg-white/20 text-white px-1.5 py-0.5" data-testid="current-user-role">{ROLE_LABELS[user?.role] || user?.role}</span>
            </div>
          </div>
          <ThemeToggle />
          <button
            onClick={doLogout}
            data-testid="logout-button"
            title="Cerrar sesión"
            className="flex items-center gap-2 text-base font-bold text-white/90 hover:text-white transition-colors justify-center group-hover:justify-start w-full"
          >
            <LogOut size={18} className="shrink-0" /> <span className="hidden group-hover:inline">Cerrar sesión</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 min-h-screen data-emphasis ml-20">{children}</main>
    </div>
  );
}

export function PageHeader({ overline, title, action }) {
  return (
    <div className="flex items-end justify-between border-b border-[#E3E8EE] dark:border-[#1D3454] px-10 py-8 bg-[#EEF2F6] dark:bg-[#0A1626]">
      <div>
        <div className="overline">{overline}</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#1C1A17] dark:text-[#E7EDF6] mt-1">
          {title}
        </h1>
      </div>
      {action}
    </div>
  );
}
